import AttachmentRounded from '@mui/icons-material/AttachmentRounded';
import Cancel from '@mui/icons-material/Cancel';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Close from '@mui/icons-material/Close';
import Warning from '@mui/icons-material/Warning';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import { inputAdornmentClasses } from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers-pro';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Label from '~components/FormFields/v2/Label';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import {
  ChecklistItem,
  DriverContext,
  StagedComplianceDoc,
} from '~hooks/useComplianceDocuments/checklist';
import { CompliantType } from '~hooks/useComplianceDocuments/models';
import useComplianceDocuments from '~hooks/useComplianceDocuments/useComplianceDocuments';
import {
  ComplianceDocumentType,
  FieldType,
} from '~hooks/useComplianceDocumentTypes/models';
import useComplianceDocumentTypes from '~hooks/useComplianceDocumentTypes/useComplianceDocumentTypes';
import { FileAttachment } from '~hooks/useFileAttachment/models';
import { useFileAttachment } from '~hooks/useFileAttachment/useFileAttachment';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  buildStagedChecklist,
  isDocumentTypeRequiredForEntity,
  toDateTimeString,
} from './compliancePanelUtils';

interface Props {
  compliantType: CompliantType;
  compliantId?: string;
  companyId?: string;
  managedCompanyId?: string;
  driverContext?: DriverContext;
  stagedMode?: boolean;
  onStagedDocsChange?: (docs: StagedComplianceDoc[]) => void;
  onComplianceChange?: () => void;
  onEditingStateChange?: (isEditing: boolean) => void;
}

const STATUS_CONFIG = {
  valid: {
    icon: <CheckCircle sx={{ color: 'success.main', fontSize: 18 }} />,
    color: 'success' as const,
    label: 'administration.compliance_document_types.status_valid',
  },
  expiring: {
    icon: <Warning sx={{ color: 'warning.main', fontSize: 18 }} />,
    color: 'warning' as const,
    label: 'administration.compliance_document_types.status_expiring',
  },
  expired: {
    icon: <Cancel sx={{ color: 'error.main', fontSize: 18 }} />,
    color: 'error' as const,
    label: 'administration.compliance_document_types.status_expired',
  },
  missing: {
    icon: <Cancel sx={{ color: 'error.main', fontSize: 18 }} />,
    color: 'error' as const,
    label: 'administration.compliance_document_types.status_missing',
  },
  optional: {
    icon: null,
    color: 'default' as const,
    label: 'administration.compliance_document_types.status_optional',
  },
};

// 10 MB max file size
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ACCEPTED_FILE_TYPES = '.pdf,.png,.jpg,.jpeg';

function getValueLabel(documentType: ComplianceDocumentType): string {
  switch (documentType.fieldType) {
    case 'number':
      return t('administration.compliance_document_types.value_number');
    case 'date':
      return t('administration.compliance_document_types.value_date');
    case 'boolean':
      return t('administration.compliance_document_types.value_boolean');
    default:
      return t('administration.compliance_document_types.value_text');
  }
}

function getFieldTypeSuffix(fieldType: FieldType): string | null {
  switch (fieldType) {
    case 'string':
      return t('administration.compliance_document_types.field_type_text');
    case 'number':
      return t('administration.compliance_document_types.field_type_number');
    case 'date':
      return t('administration.compliance_document_types.field_type_date');
    case 'boolean':
      return t('administration.compliance_document_types.field_type_boolean');
    default:
      return null;
  }
}

function getInputType(fieldType: FieldType): string {
  switch (fieldType) {
    case 'number':
      return 'number';
    case 'date':
      return 'date';
    default:
      return 'text';
  }
}

interface CardEditState {
  value: string;
  expiresAt: string;
  file: File | null;
  removeExistingAttachment: boolean;
  isSaving: boolean;
}

interface ComplianceCardProps {
  item: ChecklistItem;
  editState: CardEditState;
  attachedFile: FileAttachment | null;
  isRequiredForEntity: boolean;
  isEditing: boolean;
  isDirty: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onValueChange: (value: string) => void;
  onExpiresAtChange: (value: string) => void;
  onFileChange: (file: File | null) => void;
  onRemoveExistingAttachment: () => void;
  onSave: () => void;
  onRemove: () => void;
  onFileOpen: (url: string) => void;
  onFileSizeError: () => void;
}

function ComplianceCard({
  item,
  editState,
  attachedFile,
  isRequiredForEntity,
  isEditing,
  isDirty,
  onEdit,
  onCancel,
  onValueChange,
  onExpiresAtChange,
  onFileChange,
  onRemoveExistingAttachment,
  onSave,
  onRemove,
  onFileOpen,
  onFileSizeError,
}: ComplianceCardProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const statusConfig = STATUS_CONFIG[item.status as keyof typeof STATUS_CONFIG];
  const showValueField = item.documentType.fieldType !== 'none';
  const showExpirationField = item.documentType.hasExpiration;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (selectedFile.size > MAX_FILE_SIZE) {
      onFileSizeError();
      return;
    }
    onFileChange(selectedFile);
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const formattedExpiration = editState.expiresAt
    ? dayjs.utc(editState.expiresAt).format('MM/DD/YYYY')
    : '';

  const fieldTypeSuffix = getFieldTypeSuffix(item.documentType.fieldType);
  const cardTitle = fieldTypeSuffix
    ? `${item.documentType.name} (${fieldTypeSuffix})`
    : item.documentType.name;

  // --- Read-only mode ---
  if (!isEditing) {
    return (
      <Card variant="outlined" sx={{ px: 2, py: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: attachedFile ? 1 : 0,
          }}
        >
          <Box sx={{ display: 'flex', gap: 3, minWidth: 0 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                noWrap
                sx={{
                  fontWeight: 600,
                }}
              >
                {item.documentType.name}
              </Typography>
              {fieldTypeSuffix && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {fieldTypeSuffix}
                </Typography>
              )}
            </Box>

            {showValueField && (
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {t('administration.compliance_document_types.value_label')}
                </Typography>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    color: editState.value ? 'text.primary' : 'text.disabled',
                    fontStyle: editState.value ? 'normal' : 'italic',
                  }}
                >
                  {editState.value
                    ? item.documentType.fieldType === 'date'
                      ? dayjs.tz(editState.value).format('MM/DD/YYYY')
                      : editState.value
                    : t('common.not_specified')}
                </Typography>
              </Box>
            )}

            {showExpirationField && (
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {t('administration.compliance_document_types.expires')}
                </Typography>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    color: formattedExpiration ? 'text.primary' : 'text.disabled',
                    fontStyle: formattedExpiration ? 'normal' : 'italic',
                  }}
                >
                  {formattedExpiration || t('common.not_specified')}
                </Typography>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexShrink: 0,
              pt: 0.5,
            }}
          >
            {statusConfig.icon}
            <SmallButton variant="outlined" color="secondary" onClick={onEdit}>
              {t('actions.edit')}
            </SmallButton>
          </Box>
        </Box>
        {attachedFile && (
          <PseudoLink
            title={attachedFile.fileName}
            action={() => onFileOpen(attachedFile.fileUrl)}
          />
        )}
      </Card>
    );
  }

  // --- Edit mode ---
  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: showValueField || showExpirationField ? 2 : 0,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
          }}
        >
          {cardTitle}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            icon={statusConfig.icon ?? undefined}
            label={t(statusConfig.label)}
            color={statusConfig.color}
            size="small"
            variant="outlined"
          />
          {!isRequiredForEntity && (item.document || item.stagedDoc) && (
            <SmallButton
              color="error"
              variant="outlined"
              onClick={onRemove}
              disabled={editState.isSaving}
            >
              {t('administration.compliance_document_types.remove')}
            </SmallButton>
          )}
          <SmallButton
            variant="outlined"
            color="secondary"
            onClick={onCancel}
            disabled={editState.isSaving}
          >
            {t('actions.cancel')}
          </SmallButton>
          <SmallLoadingButton
            variant="contained"
            onClick={onSave}
            disabled={!isDirty}
            loading={editState.isSaving}
          >
            {t('administration.compliance_document_types.save')}
          </SmallLoadingButton>
        </Box>
      </Box>
      {(showValueField || showExpirationField) && (
        <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
          {showValueField && (
            <FormControl
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
                flex: 1,
              }}
            >
              <Label>{getValueLabel(item.documentType as ComplianceDocumentType)}</Label>
              {item.documentType.fieldType === 'date' ? (
                <DatePicker
                  enableAccessibleFieldDOMStructure={false}
                  onChange={(newValue) => {
                    onValueChange(
                      newValue?.isValid() ? newValue.format('YYYY-MM-DD') : '',
                    );
                  }}
                  value={editState.value ? dayjs.tz(editState.value) : null}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      slotProps: {
                        htmlInput: { sx: { fontSize: '12px', height: '23px' } },
                      },
                      placeholder: t(
                        'administration.compliance_document_types.date_placeholder',
                      ),
                      variant: 'outlined',
                      sx: {
                        [`& .${inputAdornmentClasses.root}`]: {
                          ml: 0,
                        },
                      },
                    },
                  }}
                />
              ) : (
                <TextField
                  fullWidth
                  type={getInputType(item.documentType.fieldType)}
                  value={editState.value}
                  onChange={(e) => onValueChange(e.target.value)}
                />
              )}
            </FormControl>
          )}

          {showExpirationField && (
            <FormControl
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
                flex: 1,
              }}
            >
              <Label>
                {t('administration.compliance_document_types.expiration_date')}
              </Label>
              <DatePicker
                enableAccessibleFieldDOMStructure={false}
                onChange={(newValue) => {
                  onExpiresAtChange(
                    newValue?.isValid() ? newValue.format('YYYY-MM-DD') : '',
                  );
                }}
                value={editState.expiresAt ? dayjs.utc(editState.expiresAt) : null}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    slotProps: {
                      htmlInput: { sx: { fontSize: '12px', height: '23px' } },
                    },
                    placeholder: t(
                      'administration.compliance_document_types.date_placeholder',
                    ),
                    variant: 'outlined',
                    sx: {
                      [`& .${inputAdornmentClasses.root}`]: {
                        ml: 0,
                      },
                    },
                  },
                }}
              />
            </FormControl>
          )}
        </Stack>
      )}
      {/* File section */}
      <Box>
        {attachedFile && !editState.file && !editState.removeExistingAttachment && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PseudoLink
              title={attachedFile.fileName}
              action={() => onFileOpen(attachedFile.fileUrl)}
            />
            <IconButton
              size="small"
              onClick={onRemoveExistingAttachment}
              aria-label={t('administration.compliance_document_types.remove_file')}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        )}

        {editState.file && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">{editState.file.name}</Typography>
            <IconButton size="small" onClick={() => onFileChange(null)}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        )}

        {!editState.file && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_FILE_TYPES}
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <SmallButton
              variant="outlined"
              color="secondary"
              startIcon={<AttachmentRounded />}
              onClick={() => fileInputRef.current?.click()}
            >
              {t('administration.compliance_document_types.upload_file')}
            </SmallButton>
          </>
        )}
      </Box>
    </Card>
  );
}

export function CompliancePanel({
  compliantType,
  compliantId,
  companyId: companyIdProp,
  managedCompanyId,
  driverContext,
  stagedMode,
  onStagedDocsChange,
  onComplianceChange,
  onEditingStateChange,
}: Props) {
  const { toasterStore, userStore } = useStores();
  const ownCompanyId = getEffectiveUserCompanyId(userStore);
  const companyId = companyIdProp || ownCompanyId;
  const {
    isLoading,
    checklist,
    getChecklist,
    createDocument,
    updateDocument,
    deleteDocument,
  } = useComplianceDocuments(companyId);
  const {
    isLoading: isLoadingDocTypes,
    complianceDocumentTypes,
    fetchAll: getAllDocTypes,
  } = useComplianceDocumentTypes();
  const { createFileAttachmentOnComplianceDocument, deleteFileAttachmentById } =
    useFileAttachment();
  const [stagedDocs, setStagedDocs] = useState<StagedComplianceDoc[]>([]);
  const [editStates, setEditStates] = useState<Record<string, CardEditState>>({});
  const [fileAttachments, setFileAttachments] = useState<Record<string, FileAttachment>>(
    {},
  );
  const [editingCards, setEditingCards] = useState<Record<string, boolean>>({});

  // Notify parent when any compliance card enters or exits edit mode
  useEffect(() => {
    onEditingStateChange?.(Object.values(editingCards).some(Boolean));
  }, [editingCards, onEditingStateChange]);

  // Normal mode: fetch checklist from API
  useEffect(() => {
    if (!stagedMode && compliantId) {
      getChecklist(compliantType, compliantId);
    }
  }, [compliantType, compliantId, stagedMode, getChecklist]);

  // Staged mode: fetch doc types. In a managed-vendor context fetch the vendor's types
  // via the managed endpoint (own company + vendor); otherwise the target company's own.
  useEffect(() => {
    if (!stagedMode) return;
    if (managedCompanyId) {
      if (ownCompanyId) {
        getAllDocTypes(ownCompanyId, managedCompanyId);
      }
    } else if (companyId) {
      getAllDocTypes(companyId);
    }
  }, [stagedMode, companyId, managedCompanyId, ownCompanyId, getAllDocTypes]);

  // Sync staged docs to parent via callback
  useEffect(() => {
    if (stagedMode) {
      onStagedDocsChange?.(stagedDocs);
    }
  }, [stagedDocs, stagedMode, onStagedDocsChange]);

  // Build file attachments map from checklist data (returned inline by API)
  useEffect(() => {
    if (!stagedMode && checklist.length > 0) {
      const nextMap: Record<string, FileAttachment> = {};
      checklist.forEach((item) => {
        if (item.fileAttachment) {
          nextMap[item.documentType.id] = {
            id: item.fileAttachment.id,
            fileName: item.fileAttachment.fileName,
            fileUrl: item.fileAttachment.fileUrl,
          } as FileAttachment;
        }
      });
      setFileAttachments(nextMap);
    } else if (!stagedMode) {
      setFileAttachments({});
    }
  }, [checklist, stagedMode]);

  const stagedChecklist = useMemo(
    () =>
      stagedMode
        ? buildStagedChecklist(
            complianceDocumentTypes,
            compliantType,
            stagedDocs,
            driverContext,
          )
        : [],
    [stagedMode, complianceDocumentTypes, compliantType, stagedDocs, driverContext],
  );

  const displayChecklist = stagedMode ? stagedChecklist : checklist;

  const hasIssues = useMemo(
    () => displayChecklist.some((item) => ['expired', 'missing'].includes(item.status)),
    [displayChecklist],
  );

  // Initialize edit states when checklist changes
  useEffect(() => {
    const newStates: Record<string, CardEditState> = {};
    displayChecklist.forEach((item) => {
      const dtId = item.documentType.id;
      newStates[dtId] = {
        value: item.stagedDoc?.value ?? item.document?.value ?? '',
        expiresAt:
          item.stagedDoc?.expiresAt ??
          (item.document?.expiresAt ? item.document.expiresAt.format('YYYY-MM-DD') : ''),
        file: null,
        removeExistingAttachment: false,
        isSaving: false,
      };
    });
    if (Object.keys(newStates).length > 0) {
      setEditStates((prev) => {
        const merged = { ...prev };
        for (const [dtId, state] of Object.entries(newStates)) {
          if (!merged[dtId]) {
            merged[dtId] = state;
          }
        }
        return merged;
      });
    }
  }, [displayChecklist]);

  const updateEditState = useCallback((dtId: string, updates: Partial<CardEditState>) => {
    setEditStates((prev) => ({
      ...prev,
      [dtId]: { ...prev[dtId], ...updates },
    }));
  }, []);

  const getOriginalValues = useCallback((item: ChecklistItem) => {
    const value = item.stagedDoc?.value ?? item.document?.value ?? '';
    const expiresAt =
      item.stagedDoc?.expiresAt ??
      (item.document?.expiresAt ? item.document.expiresAt.format('YYYY-MM-DD') : '');
    return { value, expiresAt };
  }, []);

  const computeIsDirty = useCallback(
    (item: ChecklistItem, state: CardEditState) => {
      const original = getOriginalValues(item);
      return (
        state.value !== original.value ||
        state.expiresAt !== original.expiresAt ||
        state.file !== null ||
        state.removeExistingAttachment
      );
    },
    [getOriginalValues],
  );

  const handleEdit = useCallback(
    (item: ChecklistItem) => {
      const dtId = item.documentType.id;
      const original = getOriginalValues(item);
      updateEditState(dtId, {
        value: original.value,
        expiresAt: original.expiresAt,
        file: null,
        removeExistingAttachment: false,
      });
      setEditingCards((prev) => ({ ...prev, [dtId]: true }));
    },
    [getOriginalValues, updateEditState],
  );

  const handleCancel = useCallback(
    (item: ChecklistItem) => {
      const dtId = item.documentType.id;
      const original = getOriginalValues(item);
      updateEditState(dtId, {
        value: original.value,
        expiresAt: original.expiresAt,
        file: null,
        removeExistingAttachment: false,
      });
      setEditingCards((prev) => ({ ...prev, [dtId]: false }));
    },
    [getOriginalValues, updateEditState],
  );

  const isRequiredForEntityCb = useCallback(
    (dt: ComplianceDocumentType): boolean =>
      isDocumentTypeRequiredForEntity(dt, compliantType, driverContext),
    [compliantType, driverContext],
  );

  const handleSave = useCallback(
    async (item: ChecklistItem) => {
      if (!stagedMode && !compliantId) return;
      const dtId = item.documentType.id;
      const state = editStates[dtId];
      if (!state) return;

      updateEditState(dtId, { isSaving: true });

      try {
        if (stagedMode) {
          const newDoc: StagedComplianceDoc = {
            documentTypeId: dtId,
            value: state.value || undefined,
            expiresAt: state.expiresAt || undefined,
            file: state.file ?? undefined,
          };
          setStagedDocs((prev) => {
            const idx = prev.findIndex((d) => d.documentTypeId === dtId);
            return idx >= 0
              ? prev.map((d, i) => (i === idx ? newDoc : d))
              : [...prev, newDoc];
          });
          if (state.removeExistingAttachment && !state.file) {
            setFileAttachments((prev) => {
              const next = { ...prev };
              delete next[dtId];
              return next;
            });
          }
          if (state.file) {
            setFileAttachments((prev) => {
              const existing = prev[dtId];
              if (existing?.fileUrl?.startsWith('blob:')) {
                URL.revokeObjectURL(existing.fileUrl);
              }
              return {
                ...prev,
                [dtId]: {
                  id: '',
                  fileName: state.file!.name,
                  fileUrl: URL.createObjectURL(state.file!),
                } as FileAttachment,
              };
            });
          }
          updateEditState(dtId, {
            file: null,
            removeExistingAttachment: false,
          });
          toasterStore.push(
            alert(
              t('administration.compliance_document_types.document_saved'),
              AlertTypes.success,
            ),
          );
          setEditingCards((prev) => ({
            ...prev,
            [dtId]: false,
          }));
        } else {
          if (!compliantId) return;
          let documentId: string | undefined;

          if (item.document?.id) {
            await updateDocument(compliantType, compliantId, item.document.id, {
              value: state.value || undefined,
              expires_at: toDateTimeString(state.expiresAt || undefined),
            });
            documentId = item.document.id;
          } else {
            const created = await createDocument(compliantType, compliantId, {
              compliance_document_type_id: dtId,
              value: state.value || undefined,
              expires_at: toDateTimeString(state.expiresAt || undefined),
            });
            documentId = created?.id;
          }

          // Delete existing attachment if replacing or explicitly removing
          const existingAttachment = fileAttachments[dtId];
          if (existingAttachment && (state.file || state.removeExistingAttachment)) {
            await deleteFileAttachmentById(existingAttachment.id);
            setFileAttachments((prev) => {
              const next = { ...prev };
              delete next[dtId];
              return next;
            });
          }

          // Upload new file if provided
          if (state.file && documentId) {
            const attachment = await createFileAttachmentOnComplianceDocument(
              state.file,
              documentId,
            );
            setFileAttachments((prev) => ({
              ...prev,
              [dtId]: attachment,
            }));
          }
          updateEditState(dtId, {
            file: null,
            removeExistingAttachment: false,
          });

          toasterStore.push(
            alert(
              t('administration.compliance_document_types.document_saved'),
              AlertTypes.success,
            ),
          );
          setEditingCards((prev) => ({
            ...prev,
            [dtId]: false,
          }));
          onComplianceChange?.();
          getChecklist(compliantType, compliantId);
        }
      } catch {
        toasterStore.push(
          alert(
            t('error_messages.compliance_document_types.failed_to_save'),
            AlertTypes.error,
          ),
        );
      } finally {
        updateEditState(dtId, { isSaving: false });
      }
    },
    [
      editStates,
      stagedMode,
      compliantType,
      compliantId,
      fileAttachments,
      onComplianceChange,
      getChecklist,
    ],
  );

  const handleRemove = useCallback(
    async (item: ChecklistItem) => {
      if (!stagedMode && !compliantId) return;
      const dtId = item.documentType.id;

      if (stagedMode) {
        setStagedDocs((prev) => prev.filter((d) => d.documentTypeId !== dtId));
        setEditStates((prev) => {
          const next = { ...prev };
          delete next[dtId];
          return next;
        });
        setFileAttachments((prev) => {
          const existing = prev[dtId];
          if (existing?.fileUrl?.startsWith('blob:')) {
            URL.revokeObjectURL(existing.fileUrl);
          }
          const next = { ...prev };
          delete next[dtId];
          return next;
        });
        setEditingCards((prev) => {
          const next = { ...prev };
          delete next[dtId];
          return next;
        });
        return;
      }

      if (!compliantId || !item.document?.id) return;

      try {
        // Delete attached file first if exists
        const attachment = fileAttachments[dtId];
        if (attachment) {
          await deleteFileAttachmentById(attachment.id);
          setFileAttachments((prev) => {
            const next = { ...prev };
            delete next[dtId];
            return next;
          });
        }

        await deleteDocument(compliantType, compliantId, item.document.id);
        setEditStates((prev) => {
          const next = { ...prev };
          delete next[dtId];
          return next;
        });
        setEditingCards((prev) => {
          const next = { ...prev };
          delete next[dtId];
          return next;
        });
        toasterStore.push(
          alert(
            t('administration.compliance_document_types.document_removed'),
            AlertTypes.success,
          ),
        );
        onComplianceChange?.();
        getChecklist(compliantType, compliantId);
      } catch {
        toasterStore.push(
          alert(
            t('error_messages.compliance_document_types.failed_to_delete'),
            AlertTypes.error,
          ),
        );
      }
    },
    [
      stagedMode,
      compliantType,
      compliantId,
      fileAttachments,
      onComplianceChange,
      getChecklist,
    ],
  );

  const currentIsLoading = stagedMode ? isLoadingDocTypes : isLoading;

  if (currentIsLoading && displayChecklist.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (displayChecklist.length === 0) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography variant="h6">
          {t('administration.compliance_document_types.compliance')}
        </Typography>
        <Chip
          label={
            hasIssues
              ? t('administration.compliance_document_types.non_compliant')
              : t('administration.compliance_document_types.compliant')
          }
          color={hasIssues ? 'error' : 'success'}
          size="small"
        />
      </Box>

      <Stack spacing={2}>
        {displayChecklist.map((item) => {
          const dtId = item.documentType.id;
          const state = editStates[dtId] ?? {
            value: '',
            expiresAt: '',
            file: null,
            removeExistingAttachment: false,
            isSaving: false,
          };

          return (
            <ComplianceCard
              key={dtId}
              item={item}
              editState={state}
              attachedFile={fileAttachments[dtId] ?? null}
              isRequiredForEntity={isRequiredForEntityCb(
                item.documentType as ComplianceDocumentType,
              )}
              isEditing={editingCards[dtId] ?? false}
              isDirty={computeIsDirty(item, state)}
              onEdit={() => handleEdit(item)}
              onCancel={() => handleCancel(item)}
              onValueChange={(v) => updateEditState(dtId, { value: v })}
              onExpiresAtChange={(v) => updateEditState(dtId, { expiresAt: v })}
              onFileChange={(f) => updateEditState(dtId, { file: f })}
              onRemoveExistingAttachment={() =>
                updateEditState(dtId, { removeExistingAttachment: true })
              }
              onSave={() => handleSave(item)}
              onRemove={() => handleRemove(item)}
              onFileOpen={(url) => window.open(url, '_blank', 'noopener,noreferrer')}
              onFileSizeError={() =>
                toasterStore.push(
                  alert(
                    t('error_messages.compliance_document_types.file_too_large'),
                    AlertTypes.error,
                  ),
                )
              }
            />
          );
        })}
      </Stack>
    </>
  );
}
